import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelCase_to_Sentence, Milliseconds, roundDecimals, toTitleCase, } from '@iotv/datamodel';
import { isNonNaNNumber, SortOrder } from '@iotv/iotv-v3-types';
import { Slider, Stack } from '@mui/material';
import { ChartsClipPath, ChartsLegend, ChartsXAxis, ChartsYAxis, LineChart, LinePlot } from '@mui/x-charts';
import { useEffect, useId, useState } from 'react';
import { timestampToISODateTime } from '../../../helpers/format';
const debug = process.env.REACT_APP_DEBUG && false;
export const HistoryLineChart = ({ historyObject, normals, normalsAccumulation, keys, keyNameMap, userAttributes, showLegend = true, showAxisLabels = false, range, showMarks = false, showTimelineSlider = true, }) => {
    const firstTimestamp = historyObject.getFirstTimestamp() ?? 0;
    const lastTimestamp = historyObject.getLastTimestamp() ?? 0;
    const historyDurationHrs = (lastTimestamp - firstTimestamp) / Milliseconds.HOUR;
    debug && console.log(`HistoryLineChart (with duration ${roundDecimals(historyDurationHrs, 1)} hours)`);
    const isOrdered = historyObject.isTimeOrdered(SortOrder.ASCENDING);
    const states = historyObject.getStateSnapshotArray(!isOrdered ? SortOrder.ASCENDING : undefined);
    const timestamps = states.map((s) => s.timestamp);
    const [slice, setSlice] = useState([firstTimestamp, lastTimestamp]);
    useEffect(() => {
        setSlice([firstTimestamp, lastTimestamp]);
    }, [firstTimestamp, lastTimestamp]);
    const keysSeries = keys.map((k) => {
        const defaultLabel = keyNameMap?.(k) ?? toTitleCase(camelCase_to_Sentence(k));
        const userAttribute = userAttributes?.[k];
        const attributeLabel = userAttribute !== undefined ? (typeof userAttribute.label === 'function' ? userAttribute.label() : (userAttribute.label ?? defaultLabel)) : defaultLabel;
        const label = userAttribute?.units ? `${attributeLabel} (${userAttribute.units})` : attributeLabel;
        const value = states.map((s) => s.state[k]);
        const data = userAttribute !== undefined && userAttribute.displayFn !== undefined ? value.map((v) => userAttribute.displayFn?.(v)) : value.map((v) => v ?? null);
        const s = { label, data, showMark: false, connectNulls: false };
        return s;
    });
    const normalsSeries = normals
        ? keys.map((k) => {
            const defaultLabel = toTitleCase(camelCase_to_Sentence(k));
            const userAttribute = userAttributes?.[k];
            const attributeLabel = userAttribute !== undefined
                ? typeof userAttribute.label === 'function'
                    ? userAttribute.label()
                    : (userAttribute.label ?? defaultLabel)
                : defaultLabel;
            const keyLabel = userAttribute?.units ? `${attributeLabel} (${userAttribute.units})` : attributeLabel;
            const label = `Normal ${keyLabel}`;
            /**
             * NOTE(CRA): it's somewhat weird to be comparing the current reading to the normal, if the normal includes the current reading
             * Hence, I have elected to not include it
             */
            const value = timestamps.map((t) => normals?.getNormalForInnerPeriodEveryOuterPeriod({ fromDateMs: t - Milliseconds.HOUR - Milliseconds.DAY, toDateMs: t - Milliseconds.DAY }, Milliseconds.DAY, normalsAccumulation?.[k] ?? 7, k)?.normal.mean ?? null);
            const data = userAttribute !== undefined && userAttribute.displayFn !== undefined ? value.map((v) => userAttribute.displayFn?.(v)) : value.map((v) => v ?? null);
            const s = { label, data, showMark: false, connectNulls: false };
            return s;
        })
        : [];
    const series = [...keysSeries, ...normalsSeries];
    const xAxis = {
        scaleType: 'linear',
        data: timestamps,
        valueFormatter: (t) => timestampToISODateTime(t).replace(' ', '\n'),
        height: showAxisLabels ? 60 : 40,
        domainLimit: 'strict',
        min: slice[0],
        max: slice[1],
    };
    const handleSliceChange = (event, value, activeThumb) => {
        debug && console.log('handleSliceChange', { event, value, activeThumb });
        if (value instanceof Array && value.length === 2) {
            const newValue = value;
            setSlice(newValue);
        }
    };
    const sliderProps = {
        min: firstTimestamp,
        step: Milliseconds.SECOND,
        max: lastTimestamp,
        value: slice,
        onChange: handleSliceChange,
    };
    const startIndex = historyObject.timestamp.findIndex((t) => slice[0] <= t);
    const endIndex = historyObject.timestamp.findLastIndex((t) => t <= slice[1]);
    const minY = Math.min(...keysSeries.map((s) => s.data?.slice(startIndex, endIndex).filter((n) => isNonNaNNumber(n)) ?? [0]).map((d) => Math.min(...d)));
    const maxY = Math.max(...keysSeries.map((s) => s.data?.slice(startIndex, endIndex).filter((n) => isNonNaNNumber(n)) ?? [0]).map((d) => Math.max(...d)));
    const id = useId();
    const clipPathId = `${id}-clip-path`;
    return (_jsxs(Stack, { width: "100%", height: "100%", minHeight: 200, children: [_jsxs(LineChart, { series: series, xAxis: [xAxis], yAxis: [{ min: range?.[0] ?? minY, max: range?.[1] ?? maxY, domainLimit: 'nice' }], grid: { horizontal: true }, skipAnimation: true, hideLegend: !showLegend, slotProps: { legend: { sx: { fontSize: '1em' } }, mark: { display: showMarks ? undefined : 'none' } }, children: [_jsx("g", { clipPath: `url(#${clipPathId})`, children: _jsx(LinePlot, {}) }), _jsx(ChartsClipPath, { id: clipPathId, offset: { top: 5, bottom: 5 } }), _jsx(ChartsXAxis, { label: showAxisLabels ? 'Time' : undefined }), _jsx(ChartsYAxis, { label: showAxisLabels ? keysSeries.map((s) => s.label).join(', ') : undefined }), _jsx(ChartsLegend, {})] }), showTimelineSlider && (_jsx(Stack, { paddingInline: 3, children: _jsx(Slider, { ...sliderProps }) }))] }));
};
